/**
 * Shared activity-log grid helpers (change-detail modal, JSON parsers) used by
 * CustomLogs and per-record panels such as ActivityLogsPanel.
 */
export default {
  data() {
    return {
      rowsPerPageItems: [25, 50, 100, 150, 200],
      changeDetailDialog: false,
      changeDetailTitle: '',
      changeDetailSections: [],
      tableHeaders: [
        { text: 'Description', value: 'tbl_description', sortable: false, width: '26%' },
        { text: 'Record', value: 'tbl_record', sortable: false, width: '18%' },
        { text: 'Done by', value: 'tbl_done_by', sortable: false, width: '16%' },
        { text: 'IP address', value: 'tbl_ip', sortable: false, width: '12%' },
        { text: 'Change details', value: 'tbl_details', sortable: false, width: '20%' },
        { text: 'When', value: 'created_at', sortable: false, width: '14%' },
      ],
    };
  },
  methods: {
    decodePossibleHtmlEscapedJson(str) {
      if (typeof str !== 'string') {
        return str;
      }
      const el = document.createElement('textarea');
      el.innerHTML = str;
      return el.value;
    },
    parseChangeJsonPayload(raw) {
      if (raw == null || raw === '') {
        return null;
      }
      if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
        const hasAttrs = raw.attributes && typeof raw.attributes === 'object';
        const hasOld = raw.old && typeof raw.old === 'object';
        return hasAttrs || hasOld ? raw : null;
      }
      const s = String(raw).trim();
      if (!s || s === '{}') {
        return null;
      }
      try {
        return JSON.parse(s);
      } catch (e) {
        try {
          return JSON.parse(this.decodePossibleHtmlEscapedJson(s));
        } catch (e2) {
          return null;
        }
      }
    },
    hasChangeDetailPayload(item) {
      const p = this.parseChangeJsonPayload(item && item.tbl_change_json);
      const hasClassicPayload = !!p
          && typeof p === 'object'
          && (
              (p.attributes && typeof p.attributes === 'object' && Object.keys(p.attributes).length > 0)
              || (p.old && typeof p.old === 'object' && Object.keys(p.old).length > 0)
          );
      if (hasClassicPayload) {
        return true;
      }
      return this.buildPropertiesChangeSections(item).length > 0
          || this.buildFilterDetailSections(item).length > 0
          || this.buildLegacyPropertiesSections(item).length > 0
          || this.buildActionContextSections(item).length > 0;
    },
    openChangeDetailModal(item) {
      this.changeDetailTitle = (item && item.tbl_description) ? String(item.tbl_description) : 'Change detail';
      const actionContextSections = this.buildActionContextSections(item);
      const changeSections = actionContextSections.length > 0
          ? []
          : this.buildHumanReadableChangeSections(item);
      const propertiesSections = this.buildPropertiesChangeSections(item);
      const filterSections = this.buildFilterDetailSections(item);
      const legacyPropertiesSections = this.buildLegacyPropertiesSections(item);
      this.changeDetailSections = [
        ...changeSections,
        ...propertiesSections,
        ...filterSections,
        ...legacyPropertiesSections,
        ...actionContextSections,
      ];
      this.changeDetailDialog = true;
    },
    formatChangeDetailValue(v) {
      if (v === null || v === undefined || v === '') {
        return '(empty)';
      }
      if (Array.isArray(v)) {
        return v.length ? v.map(x => this.formatChangeDetailValue(x)).join(', ') : '(empty)';
      }
      if (typeof v === 'object') {
        const readableLink = this.formatPossibleLinkObject(v);
        if (readableLink) {
          return readableLink;
        }
        return this.formatObjectPreview(v);
      }
      if (typeof v === 'boolean') {
        return v ? 'Yes' : 'No';
      }
      const s = String(v);
      if (this.looksLikeHtml(s)) {
        return this.toReadableTextPreview(s, 500);
      }
      return s;
    },
    looksLikeHtml(value) {
      const s = String(value || '');
      return /<\/?[a-z][\s\S]*>/i.test(s);
    },
    toReadableTextPreview(value, maxLen = 500) {
      const html = String(value || '');
      const div = document.createElement('div');
      div.innerHTML = html;
      const raw = (div.textContent || div.innerText || '').replace(/\s+/g, ' ').trim();
      if (!raw) {
        return '(empty)';
      }
      if (raw.length > maxLen) {
        return raw.slice(0, maxLen - 1) + '…';
      }
      return raw;
    },
    formatPossibleLinkObject(v) {
      if (!v || typeof v !== 'object' || Array.isArray(v)) {
        return '';
      }
      const url = v.url ? String(v.url).trim() : '';
      const title = v.title ? String(v.title).trim() : '';
      const moduleTitle = v.module_title ? String(v.module_title).trim() : '';
      if (!url && !title && !moduleTitle) {
        return '';
      }
      const parts = [];
      if (moduleTitle) {
        parts.push(moduleTitle);
      }
      if (title) {
        parts.push(title);
      }
      if (url) {
        parts.push(url);
      }
      return parts.join(' | ');
    },
    formatObjectPreview(v) {
      try {
        const json = JSON.stringify(v);
        if (json && json.length > 280) {
          return json.slice(0, 279) + '…';
        }
        return json || '(empty)';
      } catch (e) {
        return String(v);
      }
    },
    parseJsonPayload(raw) {
      if (raw == null || raw === '') {
        return null;
      }
      if (typeof raw === 'object' && raw !== null) {
        return raw;
      }
      const s = String(raw).trim();
      if (!s || s === '{}') {
        return null;
      }
      try {
        return JSON.parse(s);
      } catch (e) {
        try {
          return JSON.parse(this.decodePossibleHtmlEscapedJson(s));
        } catch (e2) {
          return null;
        }
      }
    },
    deepComparableValue(v) {
      if (v === null || v === undefined || v === '') {
        return '';
      }
      if (Array.isArray(v)) {
        return v.map(x => this.deepComparableValue(x)).join('||');
      }
      if (typeof v === 'object') {
        try {
          return JSON.stringify(v);
        } catch (e) {
          return String(v);
        }
      }
      return String(v);
    },
    buildPropertiesChangeSections(item) {
      const payload = this.parseJsonPayload(item && item.tbl_properties_json);
      if (!payload || typeof payload !== 'object') {
        return [];
      }
      const current = payload.form_display && typeof payload.form_display === 'object' ? payload.form_display : null;
      const previous = payload.form_display_previous && typeof payload.form_display_previous === 'object' ? payload.form_display_previous : null;
      if (!current && !previous) {
        return [];
      }

      const labels = payload.form_labels && typeof payload.form_labels === 'object' ? payload.form_labels : {};
      const keys = new Set([
        ...Object.keys(current || {}),
        ...Object.keys(previous || {}),
      ]);
      const lines = [];
      keys.forEach((key) => {
        const oldVal = previous && Object.prototype.hasOwnProperty.call(previous, key) ? previous[key] : undefined;
        const newVal = current && Object.prototype.hasOwnProperty.call(current, key) ? current[key] : undefined;
        if (this.deepComparableValue(oldVal) === this.deepComparableValue(newVal)) {
          return;
        }
        const label = labels[key] || this.titleCaseField(String(key));
        lines.push({
          label,
          from: this.formatChangeDetailValue(oldVal),
          to: this.formatChangeDetailValue(newVal),
          rawKey: key,
        });
      });

      if (!lines.length) {
        return [];
      }
      lines.sort((a, b) => a.label.localeCompare(b.label));
      const sectionName = payload.section
          ? this.titleCaseField(String(payload.section))
          : 'Form';
      return [{
        sort: 950000,
        title: `${sectionName} form`,
        subtitle: '',
        lines,
      }];
    },
    buildActionContextSections(item) {
      const payload = this.parseJsonPayload(item && item.tbl_properties_json);
      if (!payload || typeof payload !== 'object') {
        return [];
      }
      const context = payload.action_context && typeof payload.action_context === 'object'
          ? payload.action_context
          : null;
      const previousContext = payload.action_context_previous && typeof payload.action_context_previous === 'object'
          ? payload.action_context_previous
          : null;
      if (!context) {
        return [];
      }
      const labels = payload.action_labels && typeof payload.action_labels === 'object'
          ? payload.action_labels
          : {};
      const flatCurrent = this.flattenActionContext(context);
      const flatPrevious = this.flattenActionContext(previousContext || {});
      const lines = [];
      const keys = new Set([
        ...Object.keys(flatCurrent),
        ...Object.keys(flatPrevious),
      ]);
      keys.forEach((key) => {
        const oldVal = Object.prototype.hasOwnProperty.call(flatPrevious, key) ? flatPrevious[key] : undefined;
        const newVal = Object.prototype.hasOwnProperty.call(flatCurrent, key) ? flatCurrent[key] : undefined;
        if (this.deepComparableValue(oldVal) === this.deepComparableValue(newVal)) {
          return;
        }
        lines.push({
          label: labels[key] || this.titleCaseField(String(key).replace(/\./g, ' > ')),
          from: this.formatChangeDetailValue(oldVal),
          to: this.formatChangeDetailValue(newVal),
          rawKey: key,
        });
      });
      if (!lines.length) {
        return [];
      }
      lines.sort((a, b) => a.label.localeCompare(b.label));
      return [{
        sort: 960000,
        title: payload.action_title ? String(payload.action_title) : 'Action details',
        subtitle: '',
        lines,
      }];
    },
    flattenActionContext(obj, prefix = '') {
      if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
        return {};
      }
      const out = {};
      Object.keys(obj).forEach((key) => {
        const val = obj[key];
        const next = prefix ? `${prefix}.${key}` : key;
        if (val && typeof val === 'object' && !Array.isArray(val)) {
          Object.assign(out, this.flattenActionContext(val, next));
          return;
        }
        out[next] = val;
      });
      return out;
    },
    buildFilterDetailSections(item) {
      const payload = this.parseJsonPayload(item && item.tbl_properties_json);
      if (!payload || typeof payload !== 'object') {
        return [];
      }
      const filtersDisplay = payload.filters_display && typeof payload.filters_display === 'object'
          ? payload.filters_display
          : null;
      if (!filtersDisplay) {
        return [];
      }
      const labels = payload.filters_labels && typeof payload.filters_labels === 'object'
          ? payload.filters_labels
          : {};
      const lines = [];
      Object.keys(filtersDisplay).forEach((key) => {
        const value = filtersDisplay[key];
        const valueText = this.formatChangeDetailValue(value);
        if (!valueText || valueText === '(empty)') {
          return;
        }
        lines.push({
          label: labels[key] || this.titleCaseField(String(key)),
          from: '(n/a)',
          to: valueText,
          rawKey: key,
        });
      });
      if (!lines.length) {
        return [];
      }
      lines.sort((a, b) => a.label.localeCompare(b.label));
      return [{
        sort: 955000,
        title: 'Applied filters',
        subtitle: '',
        lines,
      }];
    },
    buildLegacyPropertiesSections(item) {
      const payload = this.parseJsonPayload(item && item.tbl_properties_json);
      if (!payload || typeof payload !== 'object') {
        return [];
      }
      if ((payload.action_context && typeof payload.action_context === 'object')
          || (payload.filters_display && typeof payload.filters_display === 'object')
          || (payload.form_display && typeof payload.form_display === 'object')
          || (payload.form_display_previous && typeof payload.form_display_previous === 'object')) {
        return [];
      }

      const skipKeys = new Set([
        'path',
        'ip',
        'section',
        'filters',
        'filters_display',
        'filters_labels',
        'action_context',
        'action_labels',
        'action_title',
        'form_inputs',
        'form_inputs_previous',
        'form_display',
        'form_display_previous',
        'form_labels',
        'attributes',
        'old',
      ]);
      const lines = [];
      Object.keys(payload).forEach((key) => {
        if (skipKeys.has(key)) {
          return;
        }
        const valueText = this.formatChangeDetailValue(payload[key]);
        if (!valueText || valueText === '(empty)') {
          return;
        }
        lines.push({
          label: this.titleCaseField(String(key)),
          from: '(n/a)',
          to: valueText,
          rawKey: key,
        });
      });
      if (!lines.length) {
        return [];
      }
      lines.sort((a, b) => a.label.localeCompare(b.label));
      return [{
        sort: 957000,
        title: 'Details',
        subtitle: '',
        lines,
      }];
    },
    parsePermissionsCsv(v) {
      if (v === null || v === undefined) {
        return [];
      }
      if (Array.isArray(v)) {
        return v.map(x => String(x).trim()).filter(Boolean);
      }
      const s = String(v).trim();
      if (!s) {
        return [];
      }
      return s.split(',').map(x => String(x).trim()).filter(Boolean);
    },
    shouldSkipChangeDetailKey(key) {
      return /\.(id|user_id|created_by|updated_by|deleted_at|deleted_by)$/.test(String(key));
    },
    groupMetaForChangeKey(key) {
      const k = String(key);
      const userField = k.match(/^user\.(.+)$/);
      if (userField) {
        return {
          sort: 0,
          gk: 'user:root',
          title: 'User profile',
          subtitle: '',
          fieldLabel: this.titleCaseField(userField[1]),
        };
      }
      const rel = k.match(/^(user_detail|user_bank|user_card|user_link|user_file|user_comment)\.(\d+)\.(.+)$/);
      if (rel) {
        const typeLabels = {
          user_detail: 'User detail',
          user_bank: 'Bank account',
          user_card: 'Card',
          user_link: 'Link',
          user_file: 'File',
          user_comment: 'Comment',
        };
        const t = rel[1];
        const id = rel[2];
        const field = rel[3];
        const order = { user_detail: 10000, user_bank: 20000, user_card: 30000, user_link: 40000, user_file: 50000, user_comment: 60000 };
        return {
          sort: (order[t] || 70000) + parseInt(id, 10),
          gk: `${t}:${id}`,
          title: `${typeLabels[t] || t}`,
          subtitle: '',
          fieldLabel: this.titleCaseField(field),
        };
      }
      const entityField = k.match(/^([a-z][a-z0-9_]*)\.([a-z_]+)$/);
      if (entityField) {
        const namespace = entityField[1];
        const field = entityField[2];
        return {
          sort: 800000,
          gk: `form:${namespace}`,
          title: 'Form data',
          subtitle: '',
          fieldLabel: this.titleCaseField(field),
        };
      }
      return {
        sort: 900000,
        gk: 'other',
        title: 'Other',
        subtitle: '',
        fieldLabel: k,
      };
    },
    titleCaseField(snakeOrKey) {
      let s = String(snakeOrKey).replace(/_id$/i, '').replace(/_/g, ' ').trim();
      if (!s) {
        s = String(snakeOrKey).replace(/_/g, ' ');
      }
      return s.replace(/\b\w/g, (c) => c.toUpperCase());
    },
    parseUserDetailFieldMeta(key) {
      const m = String(key).match(/^user_detail\.(\d+)\.(.+)$/);
      if (!m) {
        return null;
      }
      return {detailId: m[1], field: m[2]};
    },
    getUserDetailTypeLabel(detailId, attrs, old) {
      const typeKey = `user_detail.${detailId}.type`;
      const newType = attrs[typeKey];
      const oldType = old[typeKey];
      const typeValue = (newType !== undefined && newType !== null && String(newType).trim() !== '')
          ? newType
          : oldType;
      if (typeValue === undefined || typeValue === null || String(typeValue).trim() === '') {
        return 'Detail';
      }
      return this.titleCaseField(String(typeValue));
    },
    buildHumanReadableChangeSections(item) {
      const payload = this.parseChangeJsonPayload(item && item.tbl_change_json);
      if (!payload || typeof payload !== 'object') {
        return [];
      }
      const attrs = payload.attributes && typeof payload.attributes === 'object' ? payload.attributes : {};
      const old = payload.old && typeof payload.old === 'object' ? payload.old : {};
      const keys = new Set([...Object.keys(attrs), ...Object.keys(old)]);
      const byGroup = {};
      keys.forEach((key) => {
        if (this.shouldSkipChangeDetailKey(key)) {
          return;
        }
        const userDetailMeta = this.parseUserDetailFieldMeta(key);
        if (userDetailMeta && userDetailMeta.field === 'type') {
          return;
        }
        const newVal = Object.prototype.hasOwnProperty.call(attrs, key) ? attrs[key] : undefined;
        const oldVal = Object.prototype.hasOwnProperty.call(old, key) ? old[key] : undefined;
        let from = this.formatChangeDetailValue(oldVal);
        let to = this.formatChangeDetailValue(newVal);

        if (key === 'user.permissions') {
          const fromList = this.parsePermissionsCsv(oldVal);
          const toList = this.parsePermissionsCsv(newVal);
          const fromSet = new Set(fromList);
          const toSet = new Set(toList);
          const added = Array.from(toSet).filter(p => !fromSet.has(p)).sort();
          const removed = Array.from(fromSet).filter(p => !toSet.has(p)).sort();

          from = removed.length ? `Removed: ${removed.join(', ')}` : '(none)';
          to = added.length ? `Added: ${added.join(', ')}` : '(none)';
        }
        if (from === to) {
          return;
        }
        const meta = this.groupMetaForChangeKey(key);
        if (!byGroup[meta.gk]) {
          byGroup[meta.gk] = { sort: meta.sort, title: meta.title, subtitle: '', lines: [] };
        }
        let lineLabel = meta.fieldLabel;
        if (userDetailMeta && userDetailMeta.field === 'title') {
          lineLabel = this.getUserDetailTypeLabel(userDetailMeta.detailId, attrs, old);
        }
        byGroup[meta.gk].lines.push({
          label: lineLabel,
          from,
          to,
          rawKey: key,
        });
      });
      Object.keys(byGroup).forEach((gk) => {
        byGroup[gk].lines.sort((a, b) => a.label.localeCompare(b.label));
      });
      return Object.values(byGroup)
          .filter((s) => s.lines.length)
          .sort((a, b) => a.sort - b.sort);
    },
  },
};
